import {
  type Admission,
  type AdmissionCreateMedicalAidAuthorization,
  type AdmissionCreateRequest,
  admissionRequestSchemaFields,
} from '@quromedical/models';
import { Row } from 'components/base';
import { CrudForm } from 'components/form';
import type { Field, SelectOption } from 'components/types';
import { Card, Text } from 'design-system/base';
import type { FetcherSearchOptions } from 'design-system/components';
import type { FormikErrors } from 'formik';
import { icd10CodeFetcher } from 'integration';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { strings } from 'strings';

export type AdmissionAuthorizationData = Pick<
  AdmissionCreateRequest,
  'medicalAidAuthorization' | 'primaryIcd10Code' | 'icd10Codes'
>;

interface MedicalAidDetails {
  schemeName?: string;
  planName?: string;
}
type ICD10Fetcher = (query?: FetcherSearchOptions) => Promise<SelectOption[]>;
interface ICD10Fields {
  primaryIcd10Code: string;
  icd10Codes?: string[];
}

type AdmissionAuthorizationFormData = AdmissionCreateMedicalAidAuthorization &
  MedicalAidDetails &
  ICD10Fields;

interface AdmissionAuthorizationFormProps extends MedicalAidDetails {
  showErrors?: boolean;
  /**
   * Returns undefined if the data is incomplete or invalid. This is to simplify error checking and
   * validation at a parent level - no data means no valid data
   */
  onValidChange: (data?: AdmissionAuthorizationData) => void;
  initialValues?: Partial<Admission>;
  icd10Codes?: string[];
}

const createFields = (
  schemeName = '',
  planName = '',
  icd10CodeFetcher: ICD10Fetcher,
  values: InitialValues,
): Field<AdmissionAuthorizationFormData>[] => {
  const lastMonthDate = new Date();
  lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
  lastMonthDate.setDate(1);

  return [
    {
      subfields: [
        {
          key: 'schemeName',
          type: 'custom',
          label: strings.FormLabelScheme,
          Display: () => <Text variant="body-strong">{schemeName}</Text>,
          background: false,
        },
        {
          key: 'planName',
          type: 'custom',
          label: strings.CardLabelPlanAndOption,
          Display: () => <Text variant="body-strong">{planName}</Text>,
          background: false,
        },
      ],
    },
    {
      subfields: [
        {
          key: 'date',
          type: 'date-component',
          label: strings.AdmissionAuthorizationDateLabel,
          maskType: 'date',
          validRange: { startDate: lastMonthDate },
        },
        {
          key: 'code',
          type: 'text-box',
          label: strings.AdmissionAuthorizationCodeLabel,
        },
      ],
    },
    {
      subfields: [
        {
          key: 'primaryIcd10Code',
          type: 'combobox-single',
          label: strings.FormLabelPrimaryICD10Codes,
          fetcher: icd10CodeFetcher,
          searchable: true,
          initialSelection: values.primaryIcd10Code
            ? [
                {
                  display: values.primaryIcd10Code,
                  value: values.primaryIcd10Code,
                },
              ]
            : [],
        },
        {
          key: 'icd10Codes',
          type: 'combobox-multiple',
          label: strings.FormLabelICD10Codes,
          fetcher: icd10CodeFetcher,
          searchable: true,
          initialSelection: values.icd10Codes?.map((code) => ({
            display: code,
            value: code,
          })),
        },
      ],
    },
  ];
};

export const AdmissionAuthorizationForm: React.FC<
  AdmissionAuthorizationFormProps
> = ({
  onValidChange,
  showErrors,
  planName,
  schemeName,
  initialValues,
  icd10Codes,
}) => {
  const initialData: Partial<AdmissionAuthorizationFormData> = {
    code: initialValues?.medicalAidAuthorization?.code,
    date: initialValues?.medicalAidAuthorization?.date,
  };

  const icd10initialData = useMemo(
    () => ({
      icd10Codes,
      primaryIcd10Code: initialValues?.primaryIcd10Code?.code,
    }),
    [icd10Codes, initialValues],
  );
  const fields = useMemo(
    () =>
      createFields(schemeName, planName, icd10CodeFetcher, icd10initialData),
    [icd10initialData, planName, schemeName],
  );

  const onChange = useCallback(
    (
      data: Partial<AdmissionAuthorizationFormData>,
      _: FormikErrors<AdmissionAuthorizationFormData>,
      hasErrors: boolean,
    ) => {
      if (hasErrors) {
        onValidChange(undefined);
      } else {
        const validated = data as AdmissionAuthorizationFormData;
        onValidChange({
          primaryIcd10Code: validated.primaryIcd10Code,
          icd10Codes: validated.icd10Codes,
          medicalAidAuthorization: {
            code: validated.code,
            date: validated.date,
          },
        });
      }
    },
    [onValidChange],
  );

  return (
    <Card unsetZIndex>
      <Row>
        <Text variant="heading">{strings.AdmissionAuthorizationFormTitle}</Text>
      </Row>

      <CrudForm<AdmissionAuthorizationFormData>
        cardProps={{ unsetZIndex: true }}
        validateOnMount
        initialValues={{ ...initialData, ...icd10initialData }}
        fields={fields}
        validationSchema={admissionRequestSchemaFields.medicalAidAuthorization}
        showErrors={showErrors}
        onChange={onChange}
        onChangeDebounceTime={0}
        showSubmitButton={false}
        hasCard={false}
      />
    </Card>
  );
};

type InitialValues = {
  icd10Codes?: string[];
  primaryIcd10Code?: string;
};
